import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Zap, MessageCircle } from 'lucide-react';
import { api } from '../lib/api';

const SIMPLE_PRESETS = [
  { label: 'How am I doing?', prompt: 'Explain in simple terms how our current copy trading strategies are performing and what that means for my money.' },
  { label: 'Market mood?', prompt: 'Is the market in a good or bad mood right now? What does Fear & Greed and VIX mean for someone like me?' },
  { label: 'Am I safe?', prompt: 'What are the risks right now that I should be aware of? Is my portfolio protected?' },
  { label: 'Should I start?', prompt: "I'm thinking about starting copy trading. In plain English, is now a good time based on market conditions?" },
];

const ADVANCED_PRESETS = [
  { label: 'Analyze My Edge', prompt: 'Analyze the edge across all active strategies. What makes them outperform? What are the risk factors?' },
  { label: 'COT Divergences', prompt: 'Identify all sentiment divergences between institutional COT data and retail positioning. Where is the highest edge right now?' },
  { label: 'Market Regime', prompt: 'Analyze the current market regime using VIX, Fear & Greed, and COT data. Which strategies should be prioritized?' },
  { label: 'Gold Analysis', prompt: 'Deep analysis on XAU/USD: COT positioning, retail sentiment, and how S.A.S.S. strategy is positioned relative to current conditions.' },
];

function FormattedMessage({ text, isSimple }) {
  const lines = text.split('\n');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {lines.map((line, i) => {
        const t = line.trim();
        if (!t) return <div key={i} style={{ height: 6 }} />;
        if (t.startsWith('## ') || t.startsWith('### ')) {
          return <p key={i} style={{ margin: 0, fontSize: isSimple ? 15 : 14, fontWeight: 800, color: isSimple ? 'var(--pp-primary)' : 'var(--pp-accent)', letterSpacing: '0.05em' }}>{t.replace(/^#{2,3}\s+/, '')}</p>;
        }
        if (t.toLowerCase().startsWith('bottom line:') || t.startsWith('📌')) {
          return <div key={i} style={{ background: 'rgba(155,35,53,0.12)', borderLeft: '3px solid var(--pp-primary)', padding: '8px 12px' }}><p style={{ margin: 0, fontSize: 13, color: 'var(--pp-text)', fontWeight: 600, lineHeight: 1.6 }}>{t}</p></div>;
        }
        if (t.startsWith('• ') || t.startsWith('- ') || t.startsWith('* ')) {
          const txt = t.replace(/^[•\-\*]\s+/, '');
          return (
            <div key={i} style={{ display: 'flex', gap: 8, paddingLeft: 4 }}>
              <div style={{ width: 5, height: 5, background: 'var(--pp-accent)', borderRadius: '50%', marginTop: 8, flexShrink: 0 }} />
              <p style={{ margin: 0, fontSize: isSimple ? 14 : 13, color: isSimple ? 'var(--pp-text)' : 'var(--pp-text-2)', lineHeight: isSimple ? 1.7 : 1.6 }}>{txt}</p>
            </div>
          );
        }
        return <p key={i} style={{ margin: 0, fontSize: isSimple ? 14 : 13, color: isSimple ? 'var(--pp-text)' : 'var(--pp-text-2)', lineHeight: isSimple ? 1.7 : 1.6 }}>{t}</p>;
      })}
    </div>
  );
}

export default function AIPage() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(undefined);
  const [isSimple, setIsSimple] = useState(false);
  const scrollRef = useRef(null);

  // Live Chat state
  const [activeView, setActiveView] = useState('ai'); // 'ai' | 'livechat'
  const [chatData, setChatData] = useState(null); // { chat_id, messages }
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const chatScrollRef = useRef(null);
  const pollRef = useRef(null);

  // Check for existing chat + unread on mount
  useEffect(() => {
    api.getMyChat().then(data => {
      if (data && data.has_chat) {
        setChatData({ chat_id: data.chat_id, messages: data.messages || [] });
        const msgs = data.messages || [];
        const unread = msgs.some(m => m.sender === 'admin' && !m.read);
        setHasUnread(unread);
      }
    }).catch(() => {});
  }, []);

  // Poll for new messages when chat is open
  useEffect(() => {
    if (activeView === 'livechat' && chatData?.chat_id) {
      const poll = async () => {
        try {
          const data = await api.getChatMessages(chatData.chat_id);
          setChatData(cd => ({ ...cd, messages: data.messages || [] }));
          setHasUnread(false);
        } catch (e) {}
      };
      poll(); // immediate fetch
      pollRef.current = setInterval(poll, 5000);
      return () => clearInterval(pollRef.current);
    }
  }, [activeView, chatData?.chat_id]);

  // Auto-scroll chat
  useEffect(() => {
    if (chatScrollRef.current) chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
  }, [chatData?.messages?.length]);

  const startChat = async (initialMsg) => {
    setChatLoading(true);
    try {
      const res = await api.startSupportChat(initialMsg ? { message: initialMsg } : {});
      setChatData({ chat_id: res.chat_id, messages: res.messages || [] });
      setChatInput('');
    } catch (e) { console.error(e); }
    setChatLoading(false);
  };

  const sendChatMsg = async () => {
    if (!chatInput.trim() || !chatData?.chat_id) return;
    const content = chatInput.trim();
    setChatInput('');
    // Optimistic update
    setChatData(cd => ({ ...cd, messages: [...(cd.messages || []), { message_id: 'temp', sender: 'user', content, timestamp: new Date().toISOString(), read: false }] }));
    try {
      await api.sendChatMessage(chatData.chat_id, content);
      const data = await api.getChatMessages(chatData.chat_id);
      setChatData(cd => ({ ...cd, messages: data.messages || [] }));
    } catch (e) { console.error(e); }
  };

  const endChat = async () => {
    if (!chatData?.chat_id) return;
    await api.closeSupportChat(chatData.chat_id).catch(() => {});
    setChatData(null);
    clearInterval(pollRef.current);
  };

  const sendMessage = useCallback(async (text) => {
    if (!text.trim() || loading) return;
    const userMsg = { role: 'user', text: text.trim() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);
    try {
      const data = await api.aiChat({ message: text.trim(), session_id: sessionId, include_market_data: true });
      setSessionId(data.session_id);
      setMessages(prev => [...prev, { role: 'ai', text: data.response }]);
    } catch (e) {
      setMessages(prev => [...prev, { role: 'ai', text: `Error: ${e.message}` }]);
    }
    setLoading(false);
    setTimeout(() => scrollRef.current?.scrollToEnd?.({ animated: true }), 150);
  }, [loading, sessionId]);

  const presets = isSimple ? SIMPLE_PRESETS : ADVANCED_PRESETS;
  const primaryColor = isSimple ? 'var(--pp-success)' : 'var(--pp-primary)';

  return (
    <div className="page-container" style={{ display: 'flex', flexDirection: 'column', height: '100vh', paddingBottom: 0 }}>
      {/* Header + Tab Bar */}
      <div style={{ borderBottom: '1px solid var(--pp-border)', background: 'var(--pp-bg)', flexShrink: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: activeView === 'livechat' ? '#00ff88' : primaryColor }} />
            <p style={{ margin: 0, fontSize: 14, fontWeight: 800, letterSpacing: '0.15em' }}>
              {activeView === 'livechat' ? 'LIVE SUPPORT' : 'AI EDGE ANALYZER'}
            </p>
          </div>
          {activeView === 'ai' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.1em', color: !isSimple ? 'var(--pp-accent)' : 'var(--pp-text-3)' }}>PRO</span>
              <button data-testid="ai-mode-toggle" onClick={() => setIsSimple(!isSimple)} style={{ width: 36, height: 20, background: isSimple ? 'rgba(0,255,148,0.3)' : 'rgba(41,121,255,0.3)', border: '1px solid ' + (isSimple ? 'var(--pp-success)' : 'var(--pp-accent)'), borderRadius: 10, cursor: 'pointer', position: 'relative', display: 'flex', alignItems: 'center', padding: '0 3px' }}>
                <div style={{ width: 14, height: 14, borderRadius: '50%', background: isSimple ? 'var(--pp-success)' : 'var(--pp-accent)', transition: 'transform 150ms', transform: `translateX(${isSimple ? 16 : 0}px)` }} />
              </button>
              <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.1em', color: isSimple ? 'var(--pp-success)' : 'var(--pp-text-3)' }}>SIMPLE</span>
            </div>
          )}
        </div>
        {/* Tab buttons */}
        <div style={{ display: 'flex', borderTop: '1px solid var(--pp-border)' }}>
          <button data-testid="tab-ai" onClick={() => setActiveView('ai')} style={{ flex: 1, padding: '8px 0', background: 'none', border: 'none', borderBottom: activeView === 'ai' ? '2px solid var(--pp-accent)' : '2px solid transparent', color: activeView === 'ai' ? 'var(--pp-accent)' : 'var(--pp-text-3)', fontSize: 11, fontWeight: 700, cursor: 'pointer', letterSpacing: '0.08em', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
            <Zap size={12} /> AI ANALYZER
          </button>
          <button data-testid="tab-livechat" onClick={() => setActiveView('livechat')} style={{ flex: 1, padding: '8px 0', background: 'none', border: 'none', borderBottom: activeView === 'livechat' ? '2px solid #00ff88' : '2px solid transparent', color: activeView === 'livechat' ? '#00ff88' : 'var(--pp-text-3)', fontSize: 11, fontWeight: 700, cursor: 'pointer', letterSpacing: '0.08em', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, position: 'relative' }}>
            <MessageCircle size={12} /> LIVE SUPPORT
            {hasUnread && <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#00ff88', position: 'absolute', top: 6, right: '25%', boxShadow: '0 0 6px #00ff88' }} />}
          </button>
        </div>
      </div>

      {/* ────────── AI CHAT VIEW ────────── */}
      {activeView === 'ai' && (<>
      <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
        {messages.length === 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 32 }}>
            <div style={{ width: 68, height: 68, background: primaryColor + '20', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
              <Zap size={32} color={primaryColor} />
            </div>
            <h3 style={{ fontSize: 18, fontWeight: 700, textAlign: 'center', marginBottom: 8 }}>
              {isSimple ? 'Ask Anything — In Plain English' : 'AI-Powered Market Analysis'}
            </h3>
            <p style={{ fontSize: 13, color: 'var(--pp-text-3)', textAlign: 'center', lineHeight: 1.6, marginBottom: 24 }}>
              {isSimple ? "No trading experience needed. Ask questions and I'll explain everything simply." : 'Technical analysis, COT positioning, strategy optimization.'}
            </p>
            <div data-testid="ai-preset-prompts" style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {presets.map((p, i) => (
                <button key={i} data-testid={`preset-${i}`} onClick={() => sendMessage(p.prompt)} style={{ padding: 14, background: 'var(--pp-surface)', border: `1px solid ${primaryColor}30`, color: 'var(--pp-text)', fontSize: 13, fontWeight: 600, cursor: 'pointer', textAlign: 'left' }}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 14, justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start', alignItems: 'flex-start' }}>
            {msg.role === 'ai' && (
              <div style={{ width: 28, height: 28, background: primaryColor + '20', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2 }}>
                <Zap size={14} color={primaryColor} />
              </div>
            )}
            <div style={{ maxWidth: '88%', padding: 14, background: msg.role === 'user' ? `rgba(155,35,53,0.12)` : 'var(--pp-surface)', border: msg.role === 'user' ? '1px solid rgba(155,35,53,0.25)' : '1px solid var(--pp-border)' }}>
              {msg.role === 'ai' ? (
                <FormattedMessage text={msg.text} isSimple={isSimple} />
              ) : (
                <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6 }}>{msg.text}</p>
              )}
            </div>
          </div>
        ))}

        {loading && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
            <div style={{ width: 28, height: 28, background: primaryColor + '20', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Zap size={14} color={primaryColor} />
            </div>
            <div style={{ background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 16, height: 16, border: '2px solid var(--pp-text-3)', borderTopColor: primaryColor, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
              <span style={{ fontSize: 12, color: 'var(--pp-text-3)', fontStyle: 'italic' }}>{isSimple ? 'Thinking...' : 'Analyzing markets...'}</span>
            </div>
          </div>
        )}
      </div>

      {/* Input Bar */}
      <div style={{ padding: '10px 12px', borderTop: '1px solid var(--pp-border)', background: 'var(--pp-surface)', display: 'flex', gap: 8, alignItems: 'flex-end', flexShrink: 0, paddingBottom: 80 }}>
        <textarea
          data-testid="ai-chat-input"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(input); } }}
          placeholder={isSimple ? 'Ask anything in plain English...' : 'Ask about edge, positioning, strategies...'}
          style={{ flex: 1, resize: 'none', minHeight: 44, maxHeight: 100, padding: '10px 14px', fontSize: 15, lineHeight: 1.5 }}
          rows={1}
        />
        <button
          data-testid="ai-send-button"
          onClick={() => sendMessage(input)}
          disabled={!input.trim() || loading}
          style={{ width: 44, height: 44, background: (!input.trim() || loading) ? 'var(--pp-overlay)' : primaryColor, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
          </svg>
        </button>
      </div>
      </>)}

      {/* ────────── LIVE CHAT VIEW ────────── */}
      {activeView === 'livechat' && (
        <>
          {/* Chat messages area */}
          <div ref={chatScrollRef} style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
            {!chatData ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 48 }}>
                <div style={{ width: 68, height: 68, background: 'rgba(0,255,148,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                  <MessageCircle size={32} color="#00ff88" />
                </div>
                <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Live Support Chat</h3>
                <p style={{ fontSize: 13, color: 'var(--pp-text-3)', textAlign: 'center', lineHeight: 1.6, marginBottom: 24, maxWidth: 280 }}>Chat directly with the Profit Pulse team. We typically respond within minutes.</p>
                <div style={{ width: '100%', maxWidth: 340 }}>
                  <textarea data-testid="livechat-initial-input" value={chatInput} onChange={e => setChatInput(e.target.value)} placeholder="Describe how we can help..." style={{ width: '100%', minHeight: 80, resize: 'none', padding: 14, fontSize: 14, marginBottom: 12, background: 'var(--pp-surface)', border: '1px solid var(--pp-border)', color: 'var(--pp-text)' }} />
                  <button data-testid="start-livechat-btn" onClick={() => startChat(chatInput)} disabled={chatLoading} style={{ width: '100%', height: 48, background: '#00ff88', border: 'none', color: '#000', fontWeight: 800, fontSize: 13, cursor: 'pointer', letterSpacing: '0.1em' }}>{chatLoading ? 'CONNECTING...' : 'START CHAT'}</button>
                </div>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#00ff88', boxShadow: '0 0 6px #00ff88' }} />
                    <span style={{ fontSize: 11, color: '#00ff88', fontWeight: 700, letterSpacing: '0.08em' }}>SUPPORT ONLINE</span>
                  </div>
                  <button data-testid="end-chat-btn" onClick={endChat} style={{ fontSize: 10, background: 'none', border: '1px solid rgba(255,51,51,0.3)', color: '#FF3333', padding: '4px 10px', cursor: 'pointer', fontWeight: 700 }}>END CHAT</button>
                </div>
                {(chatData.messages || []).map((m, i) => (
                  <div key={m.message_id || i} style={{ display: 'flex', justifyContent: m.sender === 'user' ? 'flex-end' : 'flex-start', marginBottom: 10 }}>
                    <div style={{ maxWidth: '80%', padding: '10px 14px', background: m.sender === 'user' ? 'rgba(0,255,148,0.08)' : 'rgba(41,121,255,0.08)', border: `1px solid ${m.sender === 'user' ? 'rgba(0,255,148,0.25)' : 'rgba(41,121,255,0.25)'}` }}>
                      {m.sender === 'admin' && <p style={{ margin: '0 0 2px', fontSize: 9, fontWeight: 700, color: 'var(--pp-accent)', letterSpacing: '0.08em' }}>SUPPORT</p>}
                      <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6, color: 'var(--pp-text)' }}>{m.content}</p>
                      <p style={{ margin: '4px 0 0', fontSize: 9, color: 'var(--pp-text-3)' }}>{m.timestamp ? new Date(m.timestamp).toLocaleTimeString() : ''}</p>
                    </div>
                  </div>
                ))}
                {chatData.messages?.length === 0 && <p style={{ textAlign: 'center', color: 'var(--pp-text-3)', fontSize: 12, paddingTop: 24 }}>Chat started — type a message below</p>}
              </>
            )}
          </div>

          {/* Live chat input bar */}
          {chatData && (
            <div style={{ padding: '10px 12px', borderTop: '1px solid var(--pp-border)', background: 'var(--pp-surface)', display: 'flex', gap: 8, alignItems: 'flex-end', flexShrink: 0, paddingBottom: 80 }}>
              <textarea data-testid="livechat-input" value={chatInput} onChange={e => setChatInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChatMsg(); } }} placeholder="Type your message..." style={{ flex: 1, resize: 'none', minHeight: 44, maxHeight: 100, padding: '10px 14px', fontSize: 15, lineHeight: 1.5, background: 'var(--pp-overlay)', border: '1px solid var(--pp-border)', color: 'var(--pp-text)' }} rows={1} />
              <button data-testid="livechat-send-btn" onClick={sendChatMsg} disabled={!chatInput.trim()} style={{ width: 44, height: 44, background: chatInput.trim() ? '#00ff88' : 'var(--pp-overlay)', border: 'none', cursor: chatInput.trim() ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={chatInput.trim() ? '#000' : 'var(--pp-text-3)'} strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
